import binIcon from '@src/main/assets/icons/bin-red.svg';
import editIcon from '@src/main/assets/icons/edit-red.svg';
import DOMPurify from 'dompurify';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AppTableSingle.module.scss';

type Header = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: keyof any;
  width?: string;
};

type AppTableSingleProps<T> = {
  headers: Header[];
  data: T[];
  visibility?: boolean[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onSelectedChange?: (selected: T | null) => void;
  disableActions?: (row: T) => boolean;
  selectedRow?: T | null;
  defaultSortField?: string;
  defaultSortOrder?: 1 | -1;
};

const AppTableSingle = <T extends { id: string | number }>({
  headers,
  data,
  visibility = [],
  onEdit,
  onDelete,
  onSelectedChange,
  disableActions,
  selectedRow: selectedRowProp,
  defaultSortField,
  defaultSortOrder = 1,
}: AppTableSingleProps<T>) => {
  const { t } = useTranslation();
  const [selectedRow, setSelectedRow] = useState<T | null>(selectedRowProp ?? null);

  const renderCell = (rowData: T, field: keyof T) => {
    const value = rowData[field];

    if (!value) return null;

    if (typeof value === 'string') {
      const safeHtml = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
        ALLOWED_ATTR: [],
      });
      // eslint-disable-next-line react/no-danger
      return <span dangerouslySetInnerHTML={{ __html: safeHtml }} />;
    }

    return value as React.ReactNode;
  };

  useEffect(() => {
    if (selectedRow && !data.some((item) => item.id === selectedRow.id)) {
      setSelectedRow(null);
      onSelectedChange?.(null);
    }
  }, [data, onSelectedChange, selectedRow]);

  useEffect(() => {
    setSelectedRow(selectedRowProp ?? null);
  }, [selectedRowProp]);

  const actionBodyTemplate = (rowData: T) => {
    if (disableActions?.(rowData)) return null;

    return (
      <div className="flex gap-2">
        {onEdit && (
          <Button
            icon={<img src={editIcon} alt={t('common.edit')} width="15" />}
            className="p-button-text"
            onClick={() => onEdit(rowData)}
            tooltip={t('common.edit')}
          />
        )}
        {onDelete && (
          <Button
            icon={<img src={binIcon} alt={t('common.delete')} width="15" />}
            className="p-button-text"
            onClick={() => onDelete(rowData)}
            tooltip={t('common.delete')}
          />
        )}
      </div>
    );
  };

  const handleSelectionChange = (e: { originalEvent: React.SyntheticEvent; value: T }) => {
    const selected = e.value as T | null;
    setSelectedRow(selected);
    onSelectedChange?.(selected);
  };

  return (
    <DataTable
      value={data}
      selectionMode="single"
      selection={selectedRow}
      onSelectionChange={handleSelectionChange}
      dataKey="id"
      rows={50}
      rowsPerPageOptions={[10, 20, 50]}
      paginator
      sortField={defaultSortField}
      sortOrder={defaultSortOrder}
    >
      {headers.map((col, index) =>
        visibility[index] !== false ? (
          <Column
            key={String(col.field)}
            field={String(col.field)}
            header={col.name}
            sortable
            style={col.width ? { width: col.width } : undefined}
            body={(rowData) => renderCell(rowData, col.field as keyof T)}
          />
        ) : null,
      )}
      {(onEdit || onDelete) && (
        <Column body={actionBodyTemplate} header={t('common.actions')} style={{ width: '200px' }} />
      )}
    </DataTable>
  );
};

export default AppTableSingle;
